import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import PersonalizeDataGrid from "../../../Components/Table/PersonalizeDataGrid";
import { MAIN_URL } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { buildListPersonalizeColumns } from "../../../Utils/buildListPersonalizeColumns";
import {
  DESIGNATION_PERSONALIZE_COLUMN_KEYS,
  DESIGNATION_PERSONALIZE_CONFIG,
  DESIGNATIONS_DATAGRID_KEY,
} from "./designationListConstants";

export default function OrganizationDesignationsPersonalizeView() {
  const { userData } = useAuthStore();
  const navigate = useNavigate();
  const orgId = userData?.organization?.organization_id;
  const [previewRows, setPreviewRows] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const defaultColumns = useMemo(
    () =>
      buildListPersonalizeColumns(
        DESIGNATION_PERSONALIZE_CONFIG,
        DESIGNATION_PERSONALIZE_COLUMN_KEYS
      ),
    []
  );

  useEffect(() => {
    if (!orgId) {
      setPreviewRows([]);
      return;
    }

    let cancelled = false;

    const loadPreviewRows = async () => {
      setPreviewError(null);
      setPreviewRows(null);

      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${orgId}/designations-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: 1,
              pageSize: 10,
              sort: { field: "organization_designation_id", direction: "desc" },
              filters: [],
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Preview fetch failed (${response.status})`);
        }

        const payload = await response.json();
        const rows = Array.isArray(payload?.data) ? payload.data : [];

        if (!cancelled) {
          setPreviewRows(rows);
        }
      } catch (error) {
        console.error("Designation personalize preview fetch error:", error);
        if (!cancelled) {
          setPreviewError(error?.message || "Failed to load preview data");
          setPreviewRows([]);
        }
      }
    };

    loadPreviewRows();

    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (!orgId) {
    navigate("/organization/designation", { replace: true });
    return null;
  }

  if (previewRows === null) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ px: 3, pt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Designations — personalize columns, order, width, and visibility.
          Changes save automatically for your organization.
        </Typography>
        {previewError ? (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 0.5 }}
          >
            Preview data could not be loaded: {previewError}
          </Typography>
        ) : null}
      </Box>
      <PersonalizeDataGrid
        tableName="organization_designations"
        datagridKey={DESIGNATIONS_DATAGRID_KEY}
        filterModule="organization_designations"
        data={previewRows}
        contextId={DESIGNATIONS_DATAGRID_KEY}
        orgId={orgId}
        defaultColumns={defaultColumns}
      />
    </>
  );
}
